import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { itemAPI } from '../services/api';
import Seo, { jsonLd } from '../components/Seo';
import PublicLayout from '../components/PublicLayout';
import { OrderDetailsSkeleton, EmptyState } from '../components/skeletons';

/**
 * Public item detail at /item/:slug (accepts id as a fallback).
 * SEO / AEO: Helmet-injected title, description, canonical, OG, and a
 * Product + BreadcrumbList JSON-LD block. No price is ever shown publicly.
 */

// ─── Media Slider Component ────────────────────────────────────────────────────
function MediaSlider({ mediaList, name }) {
  const [current, setCurrent] = useState(0);
  const total = mediaList.length;

  const prev = useCallback(() => setCurrent(c => (c - 1 + total) % total), [total]);
  const next = useCallback(() => setCurrent(c => (c + 1) % total), [total]);

  if (total === 0) return null;

  const activeMedia = mediaList[current];

  // Single media item — no controls needed
  if (total === 1) {
    return (
      <div style={{
        marginBottom: 20, borderRadius: 14, overflow: 'hidden',
        border: '1.5px solid var(--border)', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 320, padding: 16,
      }}>
        {activeMedia.type === 'image' ? (
          <img
            src={activeMedia.url}
            alt={name}
            style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }}
            onError={e => { e.target.style.display = 'none'; }}
          />
        ) : (
          <video
            src={activeMedia.url}
            controls
            autoPlay
            playsInline
            preload="metadata"
            style={{ width: '100%', maxHeight: 400, display: 'block', outline: 'none', background: '#000', borderRadius: 8 }}
          />
        )}
      </div>
    );
  }

  // Multi-media slider
  return (
    <div style={{ marginBottom: 24 }}>
      {/* Main slide */}
      <div style={{
        position: 'relative', borderRadius: 14, overflow: 'hidden',
        border: '1.5px solid var(--border)', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 320, padding: 16,
      }}>
        {activeMedia.type === 'image' ? (
          <img
            key={current}
            src={activeMedia.url}
            alt={`${name} — ${current + 1}`}
            style={{
              maxWidth: '100%', maxHeight: 400, objectFit: 'contain', display: 'block',
              animation: 'fadeSlide 0.28s ease',
            }}
            onError={e => { e.target.src = 'https://placehold.co/600x400?text=📦'; }}
          />
        ) : (
          <div key={current} style={{ width: '100%', display: 'flex', justifyContent: 'center', animation: 'fadeSlide 0.28s ease' }}>
            <video
              src={activeMedia.url}
              controls
              autoPlay
              playsInline
              preload="metadata"
              style={{ width: '100%', maxHeight: 400, display: 'block', outline: 'none', background: '#000', borderRadius: 8 }}
            />
          </div>
        )}

        {/* Counter badge */}
        <div style={{
          position: 'absolute', top: 12, right: 14,
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
          backdropFilter: 'blur(4px)',
          zIndex: 10,
        }}>
          {current + 1} / {total}
        </div>

        {/* Prev arrow */}
        <button
          onClick={prev}
          aria-label="Previous slide"
          style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,0.92)', border: '1.5px solid #e5e7eb',
            borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            transition: 'background 0.15s',
            zIndex: 10,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
        >
          ‹
        </button>

        {/* Next arrow */}
        <button
          onClick={next}
          aria-label="Next slide"
          style={{
            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
            background: 'rgba(255,255,255,0.92)', border: '1.5px solid #e5e7eb',
            borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            transition: 'background 0.15s',
            zIndex: 10,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
        >
          ›
        </button>
      </div>

      {/* Thumbnail strip */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 4 }}>
        {mediaList.map((m, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            style={{
              flexShrink: 0, width: 64, height: 64, border: `2px solid ${idx === current ? '#1B5C3A' : '#e5e7eb'}`,
              borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#fff', padding: 0,
              boxShadow: idx === current ? '0 0 0 2px rgba(27,92,58,0.2)' : 'none',
              transition: 'border-color 0.15s, box-shadow 0.15s',
              position: 'relative',
            }}
            aria-label={`View media ${idx + 1}`}
          >
            {m.type === 'image' ? (
              <img
                src={m.url}
                alt={`Thumbnail ${idx + 1}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                onError={e => { e.target.src = 'https://placehold.co/64x64?text=📦'; }}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', background: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', position: 'relative' }}>
                <span style={{ fontSize: 20 }}>🎬</span>
                <div style={{
                  position: 'absolute', bottom: 2, right: 2,
                  background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 8,
                  padding: '1px 3px', borderRadius: 2, fontWeight: 700
                }}>
                  VIDEO
                </div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 10 }}>
        {mediaList.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrent(idx)}
            style={{
              width: idx === current ? 20 : 8,
              height: 8, borderRadius: 99, border: 'none',
              background: idx === current ? '#1B5C3A' : '#d1d5db',
              cursor: 'pointer', padding: 0,
              transition: 'width 0.25s ease, background 0.2s',
            }}
            aria-label={`Go to media ${idx + 1}`}
          />
        ))}
      </div>

      <style>{`
        @keyframes fadeSlide {
          from { opacity: 0; transform: translateX(6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function ItemDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const lookup = /^[a-f0-9]{24}$/i.test(slug) ? itemAPI.get(slug) : itemAPI.bySlug(slug);
    lookup
      .then(r => setItem(r.data.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <PublicLayout>
      <OrderDetailsSkeleton />
    </PublicLayout>
  );
  if (notFound || !item) return (
    <PublicLayout>
      <div style={{ maxWidth: 600, margin: '60px auto', padding: 24 }}>
        <Seo title="Item not found" noindex />
        <EmptyState
          title="We couldn't find that item"
          description="It may have been removed or the link is out of date."
          actionLabel="Browse the full catalog"
          onAction={() => navigate('/catalog')}
        />
      </div>
    </PublicLayout>
  );

  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Catalog', url: '/catalog' },
    item.categoryId?.name ? { name: item.categoryId.name, url: `/c/${item.categoryId.slug || item.categoryId._id}` } : null,
    { name: item.name, url: `/item/${item.slug || item._id}` },
  ].filter(Boolean);

  const postDemand = () => navigate(`/post-demand?itemId=${item._id}`);

  // Resolve the media arrays (images and videos) into a unified list
  const allImages = Array.isArray(item.images) && item.images.length > 0
    ? item.images
    : (item.image ? [item.image] : []);
  const allVideos = Array.isArray(item.videos) ? item.videos : [];

  const mediaList = [
    ...allImages.map(url => ({ type: 'image', url })),
    ...allVideos.map(url => ({ type: 'video', url }))
  ];

  return (
    <PublicLayout>
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 64px)' }}>
      <Seo
        title={item.seoTitle || item.name}
        description={item.seoDescription || item.description}
        canonical={`/item/${item.slug || item._id}`}
        type="product"
        image={item.image || undefined}
        keywords={item.seoKeywords}
        jsonLd={[jsonLd.requestable(item), jsonLd.breadcrumbs(breadcrumbs)]}
      />

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px' }}>
        <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 18 }}>
          {breadcrumbs.map((b, i) => (
            <React.Fragment key={b.url}>
              {i > 0 && <span style={{ margin: '0 6px' }}>›</span>}
              {i < breadcrumbs.length - 1
                ? <Link to={b.url} style={{ color: 'var(--muted)', textDecoration: 'none' }}>{b.name}</Link>
                : <span style={{ color: 'var(--char)' }}>{b.name}</span>}
            </React.Fragment>
          ))}
        </nav>

        <article style={{ background: '#fff', borderRadius: 16, border: '1.5px solid var(--border)', padding: 32 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {item.categoryId?.name && <span className="tag tag-green" style={{ fontSize: 11 }}>{item.categoryId.name}</span>}
            {item.subCategoryId?.name && <span className="tag" style={{ background: 'var(--gold-lt)', color: 'var(--gold-dk)', fontSize: 11 }}>{item.subCategoryId.name}</span>}
            {item.brandId?.name && <span className="tag" style={{ background: 'var(--gray-lt)', color: 'var(--muted)', fontSize: 11 }}>{item.brandId.name}</span>}
          </div>

          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 34, fontWeight: 900, lineHeight: 1.2, color: 'var(--char)', marginBottom: 20 }}>{item.name}</h1>

          {/* Media slider — shows images and videos in a unified carousel */}
          <MediaSlider mediaList={mediaList} name={item.name} />

          {item.description && (
            <p style={{ fontSize: 15, color: 'var(--char)', lineHeight: 1.7, marginBottom: 20 }}>{item.description}</p>
          )}

          <div style={{ background: 'var(--cream)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
            🏷️ This is a reverse-bidding item. We don't display a price — you post your budget and verified vendors compete to match it.
          </div>

          {item.specs && Object.keys(item.specs).length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 18, marginBottom: 10 }}>Specifications</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <tbody>
                  {Object.entries(item.specs).map(([k, v]) => (
                    <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
                      <th scope="row" style={{ textAlign: 'left', padding: '8px 0', color: 'var(--muted)', fontWeight: 500, textTransform: 'capitalize' }}>{k}</th>
                      <td style={{ padding: '8px 0', color: 'var(--char)' }}>{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={postDemand} className="btn btn-pri btn-lg" style={{ flex: 1, minWidth: 200 }}>
              🚀 Post Demand — Name Your Price
            </button>
            <Link to={`/c/${item.categoryId?.slug || item.categoryId?._id || ''}`} className="btn btn-out btn-lg" style={{ border: '1.5px solid var(--border)', minWidth: 180, textAlign: 'center' }}>
              More in {item.categoryId?.name || 'catalog'}
            </Link>
          </div>
        </article>
      </div>
    </div>
    </PublicLayout>
  );
}
